/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo, useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';

import { PageOverlay } from '../../components/PageOverlay/PageOverlay';
import { BasicRoutes } from '../../constants/basicRoutes';
import { RootState } from '../../store';
import { closeCreateProjectOverlay } from '../../store/createProject/actions';
import { prepareRoutePath } from '../../utils/prepareRoutePath';
import { CreateProjectOverlayForm } from './CreateProjectOverlay.Form';
import { OnProjectCreateCallback } from './CreateProjectOverlay.types';

const mapState = (state: RootState) => ({
  createProject: state.createProject,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;

type CreateProjectOverlayProps = PropsFromRedux;

const CreateProjectOverlayComponent = memo<CreateProjectOverlayProps>(props => {
  const { createProject, dispatch } = props;
  const navigate = useNavigate();

  const { isOpen } = createProject;

  const close = useCallback(() => {
    dispatch(closeCreateProjectOverlay());
  }, [dispatch]);

  const onProjectCreate = useCallback<OnProjectCreateCallback>(
    (project, params) => {
      close();
      void navigate(
        prepareRoutePath(BasicRoutes.PROJECT_PAGE, {
          ':organization_id': params.organizationId,
          ':project_id': project.id,
        }),
      );
    },
    [close, navigate],
  );

  return (
    <PageOverlay
      open={isOpen}
      classes={{ childrenBlock: 'd-flex' }}
      showCloseBtn
      onCloseBtnClick={close}
      disableRestoreFocus
      name="create-project"
    >
      {isOpen ? <CreateProjectOverlayForm onProjectCreate={onProjectCreate} /> : null}
    </PageOverlay>
  );
});

export const CreateProjectOverlay = connector(CreateProjectOverlayComponent);
